import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import { getCommerceId } from '@/lib/ownership';
import { useReserveStats } from '@/hooks/useReserveStats';
import { buildSubdomain, buildSubdomainHost } from '@/lib/domain';
import { getStoreCapabilities } from '@/lib/accountType';
import InstallAppCard from '@/components/feature/InstallAppCard';
import MyLinksSummaryCard from '@/pages/dashboard/components/MyLinksSummaryCard';
import SiteClosedBanner from '@/pages/dashboard/components/SiteClosedBanner';

interface DashboardStats {
  todayVisits: number;
  totalVisits: number;
  todayOrders: number;
  todayRevenue: number;
  totalCustomers: number;
  aiSearches: number;
  aiSearchUnread: number;
}

export default function DashboardPage() {
  const { user } = useAuth();
  const merchantId = user ? getCommerceId(user) : null;
  const { stats: reserveStats, loading: reserveLoading } = useReserveStats(merchantId);
  const [stats, setStats] = useState<DashboardStats>({
    todayVisits: 0,
    totalVisits: 0,
    todayOrders: 0,
    todayRevenue: 0,
    totalCustomers: 0,
    aiSearches: 0,
    aiSearchUnread: 0,
  });
  const [statsLoading, setStatsLoading] = useState(true);
  const [customDomain, setCustomDomain] = useState<string | null>(null);
  const [domainActive, setDomainActive] = useState(false);
  const [copied, setCopied] = useState(false);
  const [mylinksCopied, setMylinksCopied] = useState(false);
  // Onboarding : nombre de produits / services pour détecter un compte vide.
  const [contentCounts, setContentCounts] = useState<{ products: number; services: number } | null>(null);
  // Onboarding « vivant » : étapes détectées automatiquement.
  const [storeDone, setStoreDone] = useState(false);
  const [paymentDone, setPaymentDone] = useState(false);

  useEffect(() => {
    if (!user) return;

    const fetchStats = async () => {
      setStatsLoading(true);
      try {
        const todayStart = new Date();
        todayStart.setHours(0, 0, 0, 0);
        const todayISO = todayStart.toISOString();
        const commerceId = getCommerceId(user);

        const [visitRes, totalVisitRes, commandeRes, aiRes, aiUnreadRes] = await Promise.all([
          supabase
            .from('visitesiteweb')
            .select('*', { count: 'exact', head: true })
            .eq('boutique_nom', user.nomcommerce)
            .gte('date', todayISO),
          supabase
            .from('visitesiteweb')
            .select('*', { count: 'exact', head: true })
            .eq('boutique_nom', user.nomcommerce),
          commerceId
            ? supabase
                .from('commande')
                .select('nomclient, email, tel, user_id, totalttc, date_time')
                .eq('idvendeur', commerceId)
                .order('date_time', { ascending: false })
                .limit(1000)
            : Promise.resolve({ data: [] as Array<{ nomclient: string | null; email: string | null; tel: string | null; user_id: string | null; totalttc: string | null; date_time: string | null }> }),
          supabase
            .from('core_notifications')
            .select('*', { count: 'exact', head: true })
            .eq('recipient_user_id', user.id)
            .eq('type', 'ai_customer_search'),
          supabase
            .from('core_notifications')
            .select('*', { count: 'exact', head: true })
            .eq('recipient_user_id', user.id)
            .eq('type', 'ai_customer_search')
            .eq('status', 'pending'),
        ]);

        const rows = (commandeRes.data || []) as Array<{
          nomclient: string | null;
          email: string | null;
          tel: string | null;
          user_id: string | null;
          totalttc: string | null;
          date_time: string | null;
        }>;

        let todayOrders = 0;
        let todayRevenue = 0;
        const seen = new Set<string>();
        for (const c of rows) {
          const d = c.date_time ? new Date(c.date_time) : null;
          if (d && d >= todayStart) {
            todayOrders += 1;
            const amount = parseFloat(String(c.totalttc ?? '0'));
            if (!Number.isNaN(amount)) todayRevenue += amount;
          }
          const key = (c.user_id || c.email || c.tel || c.nomclient || '').trim().toLowerCase();
          if (key) seen.add(key);
        }

        setStats({
          todayVisits: visitRes.count || 0,
          totalVisits: totalVisitRes.count || 0,
          todayOrders,
          todayRevenue,
          totalCustomers: seen.size,
          aiSearches: aiRes.count || 0,
          aiSearchUnread: aiUnreadRes.count || 0,
        });
      } catch {
        // Keep defaults
      } finally {
        setStatsLoading(false);
      }
    };

    fetchStats();
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const commerceId = getCommerceId(user);
    if (!commerceId) return;
    let cancelled = false;
    (async () => {
      try {
        const [prod, serv] = await Promise.all([
          supabase.from('product_items').select('id', { count: 'exact', head: true }).eq('idcommerce', commerceId),
          supabase.from('nospartenairesservices').select('id', { count: 'exact', head: true }).eq('idcommerce', commerceId),
        ]);
        if (!cancelled) setContentCounts({ products: prod.count || 0, services: serv.count || 0 });
      } catch {
        if (!cancelled) setContentCounts({ products: 0, services: 0 });
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // Onboarding « vivant » : détecte les étapes déjà réalisées (boutique
  // personnalisée, moyen de paiement actif) pour cocher la liste.
  useEffect(() => {
    if (!user) return;
    const commerceId = getCommerceId(user);
    if (!commerceId) return;
    let cancelled = false;
    (async () => {
      try {
        const [homeRes, themeRes, txRes] = await Promise.all([
          supabase.from('merchant_home_config').select('idcommerce').eq('idcommerce', commerceId).maybeSingle(),
          supabase.from('sitewebthemeactuelle').select('id').eq('idcommerce', commerceId).eq('status', 'active').limit(1).maybeSingle(),
          supabase.from('transactions').select('id').eq('id_receiver', commerceId).limit(1).maybeSingle(),
        ]);
        if (cancelled) return;
        setStoreDone(Boolean(homeRes.data) || Boolean(themeRes.data));
        setPaymentDone(Boolean(txRes.data));
      } catch {
        /* best-effort */
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  useEffect(() => {
    if (!user) return;
    supabase
      .from('websitedomain')
      .select('domaine, verified, is_active, hostname_status, ssl_status')
      .eq('user_id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data?.domaine) {
          setCustomDomain(data.domaine.replace(/^https?:\/\//, '').replace(/\/+$/, ''));
          setDomainActive(!!data.is_active);
        }
      })
      .catch(() => {});
  }, [user]);

  const copyShopUrl = (url: string) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const myLinksUrl = user?.user_name ? `${buildSubdomain(user.user_name)}/mylinks` : '';

  const copyMyLinks = () => {
    if (!myLinksUrl) return;
    navigator.clipboard.writeText(myLinksUrl).then(() => {
      setMylinksCopied(true);
      setTimeout(() => setMylinksCopied(false), 2200);
    }).catch(() => {});
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line animate-spin text-3xl text-primary-500 mb-4 block"></i>
          <p className="text-foreground-500">Chargement...</p>
        </div>
      </div>
    );
  }


  const statCards = [
    {
      icon: 'ri-eye-line',
      label: 'Visites aujourd\'hui',
      value: statsLoading ? '...' : stats.todayVisits.toLocaleString(),
      sub: `${stats.totalVisits.toLocaleString()} visites totales`,
      color: 'bg-primary-100 text-primary-700',
      link: '/dashboard/analytics',
    },
    {
      icon: 'ri-file-list-3-line',
      label: 'Commandes du jour',
      value: statsLoading ? '...' : stats.todayOrders.toLocaleString(),
      sub: 'Nouvelles commandes',
      color: 'bg-accent-100 text-accent-700',
      link: '/dashboard/orders',
    },
    {
      icon: 'ri-money-dollar-circle-line',
      label: 'Revenus du jour',
      value: statsLoading ? '...' : `${stats.todayRevenue.toLocaleString()} ${user.monaie}`,
      sub: `Solde: ${(user.solde || 0).toLocaleString()} ${user.monaie}`,
      color: 'bg-secondary-100 text-secondary-700',
      link: '/dashboard/payments',
    },
    {
      icon: 'ri-user-line',
      label: 'Total clients',
      value: statsLoading ? '...' : stats.totalCustomers.toLocaleString(),
      sub: 'Clients enregistrés',
      color: 'bg-primary-50 text-primary-600',
      link: '/dashboard/customers',
    },
    {
      icon: 'ri-robot-2-line',
      label: 'Recherches IA',
      value: statsLoading ? '...' : stats.aiSearches.toLocaleString(),
      sub: stats.aiSearchUnread > 0 ? `${stats.aiSearchUnread} nouvelle(s)` : 'Demandes clients via l’IA',
      color: 'bg-accent-100 text-accent-700',
      link: '/dashboard/socle?tab=notifications',
    },
  ];

  // Capacités du site public selon le type de compte (Mixte = produits + services).
  const caps = getStoreCapabilities(user.type, user.typecompte);

  return (
    <div className="p-4 md:p-6">
      {/* Site fermé : bandeau + réactivation en un clic */}
      <SiteClosedBanner userId={user?.id} />

      {/* Récap devise */}
      <div className="flex items-center justify-between gap-3 mb-5 px-4 py-3 rounded-lg border border-background-200/70 bg-background-50">
        <div className="flex items-center gap-3 min-w-0">
          <span className="w-9 h-9 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center flex-shrink-0">
            <i className="ri-money-dollar-circle-line"></i>
          </span>
          <div className="min-w-0">
            <p className="text-xs text-foreground-500">Devise de votre compte</p>
            <p className="text-sm font-semibold text-foreground-900 truncate">
              {user.monaie} <span className="font-normal text-foreground-500">— utilisée partout : boutique, apps et factures</span>
            </p>
          </div>
        </div>
        <Link
          to="/dashboard/settings?tab=profile"
          className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium bg-secondary-500 text-background-50 hover:bg-secondary-600 transition-colors cursor-pointer whitespace-nowrap flex-shrink-0"
        >
          <i className="ri-edit-line"></i>Modifier
        </Link>
      </div>

      {/* Welcome */}
      <div className="mb-6 md:mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-1">
            Bonjour, {user.name.split(' ')[0]} !
          </h2>
          <p className="text-sm text-foreground-500">
            Voici un aperçu de votre activité sur {user.nomcommerce}
          </p>
        </div>
        <Link
          to="/dashboard/activite"
          className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer flex-shrink-0"
        >
          <i className="ri-sun-line"></i>
          Mon activité du jour
        </Link>
      </div>

      {/* Onboarding « vivant » : étapes cochées automatiquement + progression */}
      {contentCounts && (() => {
        const contentDone = contentCounts.products > 0 || contentCounts.services > 0;
        const steps: Array<{
          n: number;
          done: boolean;
          icon: string;
          title: string;
          desc: string;
          links: Array<{ to: string; label: string; icon: string; primary: boolean }>;
        }> = [
          {
            n: 1,
            done: contentDone,
            icon: 'ri-shopping-bag-3-line',
            title: contentDone ? 'Contenu ajouté' : 'Ajoutez votre premier produit ou service',
            desc: contentDone
              ? `${contentCounts.products} produit(s) et ${contentCounts.services} service(s) publié(s) dans votre boutique.`
              : "C'est l'étape indispensable : sans contenu, votre boutique publique reste vide.",
            links: contentDone
              ? [{ to: '/dashboard/products', label: 'Gérer mes produits', icon: 'ri-settings-3-line', primary: false }]
              : [
                  { to: '/dashboard/products/new', label: 'Ajouter un produit', icon: 'ri-shopping-bag-3-line', primary: true },
                  { to: '/dashboard/services/new', label: 'Créer un service', icon: 'ri-service-line', primary: false },
                ],
          },
          {
            n: 2,
            done: storeDone,
            icon: 'ri-palette-line',
            title: 'Personnalisez votre boutique',
            desc: storeDone ? 'Votre boutique est personnalisée.' : 'Choisissez un thème et vos couleurs pour refléter votre marque.',
            links: [{ to: '/dashboard/store', label: 'Personnaliser', icon: 'ri-arrow-right-line', primary: false }],
          },
          {
            n: 3,
            done: domainActive,
            icon: 'ri-global-line',
            title: 'Connectez votre nom de domaine',
            desc: domainActive ? 'Votre nom de domaine est connecté.' : 'Utilisez votre propre adresse (ex. maboutique.com) en plus du sous-domaine.',
            links: [{ to: '/dashboard/settings?tab=domain', label: 'Configurer', icon: 'ri-arrow-right-line', primary: false }],
          },
          {
            n: 4,
            done: paymentDone,
            icon: 'ri-bank-card-line',
            title: 'Activez un moyen de paiement',
            desc: paymentDone ? 'Vous encaissez déjà des paiements en ligne.' : 'Encaissez vos commandes en ligne en toute sécurité.',
            links: [{ to: '/dashboard/payments', label: 'Activer', icon: 'ri-arrow-right-line', primary: false }],
          },
        ];
        const doneCount = steps.filter((s) => s.done).length;
        const progress = Math.round((doneCount / steps.length) * 100);
        if (doneCount >= steps.length) return null;
        return (
          <div className="mb-6 md:mb-8 bg-background-50 border border-primary-200/60 rounded-xl overflow-hidden">
            <div className="p-5 md:p-6 border-b border-background-200/70 bg-primary-50/40">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-primary-500 text-background-50 flex items-center justify-center flex-shrink-0">
                  <i className="ri-rocket-2-line text-2xl"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-lg font-bold font-heading text-foreground-950">Bienvenue sur Zifek</h3>
                    <span className="px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 text-[11px] font-semibold">
                      {doneCount} / {steps.length} étapes terminées
                    </span>
                  </div>
                  <p className="text-sm text-foreground-500 mt-1">
                    {doneCount === 0
                      ? 'Votre boutique est prête mais encore vide. Suivez ces quelques étapes pour commencer à vendre.'
                      : 'Belle progression ! Terminez les étapes restantes pour lancer votre activité.'}
                  </p>
                  <div className="mt-3">
                    <div className="h-2 rounded-full bg-background-200/70 overflow-hidden">
                      <div className="h-full rounded-full bg-primary-500 transition-all duration-500" style={{ width: `${progress}%` }}></div>
                    </div>
                    <span className="text-[11px] text-foreground-400 mt-1 inline-block">{progress}% complété</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-5 md:p-6 space-y-3">
              {steps.map((step) => (
                <div
                  key={step.n}
                  className={`rounded-lg border p-4 flex items-start gap-3 ${step.done ? 'border-accent-200/70 bg-accent-50/40' : step.n === 1 ? 'border-primary-300/70 bg-primary-50/30' : 'border-background-200/70'}`}
                >
                  <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 ${step.done ? 'bg-accent-500 text-background-50' : 'bg-primary-500 text-background-50'}`}>
                    {step.done ? <i className="ri-check-line text-base"></i> : step.n}
                  </span>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-bold text-foreground-900 flex items-center gap-2">
                      <i className={`${step.icon} text-primary-500`}></i>
                      {step.title}
                    </h4>
                    <p className="text-xs text-foreground-500 mt-0.5">{step.desc}</p>
                    <div className="flex flex-col sm:flex-row gap-2 mt-3">
                      {step.links.map((l) => (
                        <Link
                          key={l.to}
                          to={l.to}
                          className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                            l.primary
                              ? 'bg-primary-500 text-background-50 hover:bg-primary-600'
                              : 'bg-background-100 text-foreground-700 hover:bg-background-200/70'
                          }`}
                        >
                          <i className={l.icon}></i>
                          {l.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Installer l'application + notifications push */}
      <InstallAppCard variant="merchant" className="mb-6 md:mb-8" />

      {/* Badge domaine */}
      {customDomain && (
        <Link
          to="/dashboard/settings?tab=domain"
          className={`flex items-center gap-3 px-4 py-3 rounded-lg border mb-6 md:mb-8 transition-colors cursor-pointer ${domainActive ? 'bg-accent-50 border-accent-200/60' : 'bg-amber-50 border-amber-200'}`}
        >
          <span className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${domainActive ? 'bg-accent-100 text-accent-700' : 'bg-amber-100 text-amber-700'}`}>
            <i className={domainActive ? 'ri-global-line' : 'ri-time-line'}></i>
          </span>
          <div className="min-w-0 flex-1">
            <div className={`text-sm font-semibold ${domainActive ? 'text-accent-700' : 'text-amber-700'}`}>
              {domainActive ? 'Domaine connecté' : 'Domaine en attente de configuration'}
            </div>
            <div className="text-xs text-foreground-500 truncate">{customDomain}</div>
          </div>
          <span className={`text-xs font-medium whitespace-nowrap flex items-center gap-1 ${domainActive ? 'text-accent-700' : 'text-amber-700'}`}>
            Configurer<i className="ri-arrow-right-line"></i>
          </span>
        </Link>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3 md:gap-4 mb-6 md:mb-8">
        {statCards.map((stat, i) => (
          <Link
            key={i}
            to={stat.link}
            className="bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5 hover:border-background-300/60 transition-colors cursor-pointer group"
          >
            <div className="flex items-center justify-between mb-3">
              <div className={`w-9 h-9 rounded-lg ${stat.color} flex items-center justify-center`}>
                <i className={`${stat.icon} text-lg`}></i>
              </div>
              {!statsLoading && (
                <i className="ri-arrow-right-line text-foreground-300 opacity-0 group-hover:opacity-100 transition-opacity text-sm"></i>
              )}
            </div>
            <div className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-1">
              {statsLoading ? (
                <span className="inline-block w-16 h-7 bg-background-200/70 rounded animate-pulse"></span>
              ) : (
                stat.value
              )}
            </div>
            <div className="text-xs text-foreground-500">{stat.label}</div>
            {stat.sub && !statsLoading && (
              <div className="text-[11px] text-foreground-400 mt-0.5">{stat.sub}</div>
            )}
          </Link>
        ))}
      </div>

      {/* Statistiques MyLinks */}
      <MyLinksSummaryCard merchantId={merchantId} />

      {/* Statistiques du bouton Réserver */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6 mb-6 md:mb-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-foreground-700 flex items-center gap-2">
            <i className="ri-calendar-check-line text-accent-500"></i>
            Réservations — bouton « Réserver »
          </h3>
          <Link
            to="/dashboard/home-blocks"
            className="text-xs text-foreground-500 hover:text-foreground-700 flex items-center gap-1 cursor-pointer whitespace-nowrap transition-colors"
          >
            <i className="ri-settings-3-line"></i>Configurer
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
          <div className="rounded-lg bg-background-100 p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-8 h-8 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center">
                <i className="ri-eye-line"></i>
              </span>
              <span className="text-xs text-foreground-500">Visiteurs</span>
            </div>
            <div className="text-xl md:text-2xl font-bold font-heading text-foreground-950">
              {reserveLoading ? (
                <span className="inline-block w-12 h-6 bg-background-200/70 rounded animate-pulse"></span>
              ) : (
                reserveStats.visitors.toLocaleString()
              )}
            </div>
            <div className="text-[11px] text-foreground-400 mt-0.5">ont vu le bouton Réserver</div>
          </div>

          <div className="rounded-lg bg-background-100 p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-8 h-8 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center">
                <i className="ri-cursor-line"></i>
              </span>
              <span className="text-xs text-foreground-500">Appels</span>
            </div>
            <div className="text-xl md:text-2xl font-bold font-heading text-foreground-950">
              {reserveLoading ? (
                <span className="inline-block w-12 h-6 bg-background-200/70 rounded animate-pulse"></span>
              ) : (
                reserveStats.clicks.toLocaleString()
              )}
            </div>
            <div className="text-[11px] text-foreground-400 mt-0.5">clics sur le bouton Réserver</div>
          </div>

          <div className="rounded-lg bg-background-100 p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-8 h-8 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center">
                <i className="ri-calendar-check-line"></i>
              </span>
              <span className="text-xs text-foreground-500">Réservations</span>
            </div>
            <div className="text-xl md:text-2xl font-bold font-heading text-foreground-950">
              {reserveLoading ? (
                <span className="inline-block w-12 h-6 bg-background-200/70 rounded animate-pulse"></span>
              ) : (
                reserveStats.monthlyBookings.toLocaleString()
              )}
            </div>
            <div className="text-[11px] text-foreground-400 mt-0.5">créées ce mois-ci</div>
          </div>
        </div>
      </div>

      {/* Store URL */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6 mb-6 md:mb-8">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-foreground-700 flex items-center gap-2">
            <i className="ri-global-line text-primary-500"></i>
            Lien de votre boutique
          </h3>
          <Link
            to="/dashboard/settings?tab=domain"
            className="text-xs text-foreground-500 hover:text-foreground-700 flex items-center gap-1 cursor-pointer whitespace-nowrap transition-colors"
          >
            <i className="ri-settings-3-line"></i>Configurer
          </Link>
        </div>

        <div className="space-y-2">
          {/* Subdomain */}
          <div className="flex items-center justify-between gap-3 p-3 bg-background-100 rounded-lg">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-xs px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 font-medium whitespace-nowrap">Sous-domaine</span>
              <a
                href={buildSubdomain(user.user_name)}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="text-sm text-foreground-900 font-medium truncate hover:text-primary-600 transition-colors"
              >
                {buildSubdomainHost(user.user_name)}
              </a>
            </div>
            <button
              onClick={() => copyShopUrl(buildSubdomain(user.user_name))}
              className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-200/70 transition-colors cursor-pointer shrink-0"
              title="Copier le lien"
            >
              <i className={`text-sm ${copied ? 'ri-check-line text-accent-500' : 'ri-file-copy-line text-foreground-400'}`}></i>
            </button>
          </div>

          {/* Custom domain */}
          {customDomain && (
            <div className="flex items-center justify-between gap-3 p-3 bg-background-100 rounded-lg">
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-xs px-2 py-0.5 rounded-full bg-primary-50 text-primary-700 font-medium whitespace-nowrap">Domaine perso</span>
                <a
                  href={`https://${customDomain}`}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="text-sm text-foreground-900 font-medium truncate hover:text-primary-600 transition-colors"
                >
                  {customDomain}
                </a>
                {domainActive ? (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 font-medium whitespace-nowrap flex items-center gap-0.5">
                    <i className="ri-shield-check-line"></i>Connecté
                  </span>
                ) : (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium whitespace-nowrap flex items-center gap-0.5">
                    <i className="ri-time-line"></i>En attente
                  </span>
                )}
              </div>
              <button
                onClick={() => copyShopUrl(`https://${customDomain}`)}
                className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-200/70 transition-colors cursor-pointer shrink-0"
                title="Copier le lien"
              >
                <i className={`text-sm ${copied ? 'ri-check-line text-accent-500' : 'ri-file-copy-line text-foreground-400'}`}></i>
              </button>
            </div>
          )}
          {/* MyLinks — un seul lien pour les réseaux sociaux */}
          {myLinksUrl && (
            <div className="mt-2 p-3 rounded-lg bg-accent-50 border border-accent-200/50">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-7 h-7 rounded-md bg-accent-100 text-accent-700 flex items-center justify-center flex-shrink-0">
                  <i className="ri-links-line text-sm"></i>
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold text-foreground-900">Mon lien MyLinks</p>
                  <p className="text-[11px] text-foreground-500 truncate">À partager sur Instagram, Facebook, WhatsApp…</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  readOnly
                  value={myLinksUrl}
                  onFocus={(e) => e.currentTarget.select()}
                  className="flex-1 min-w-0 px-2.5 py-1.5 rounded-md bg-background-50 border border-background-200/70 text-[11px] text-foreground-600 focus:outline-none"
                />
                <button
                  onClick={copyMyLinks}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary-500 text-background-50 text-[11px] font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer flex-shrink-0"
                >
                  <i className={mylinksCopied ? 'ri-check-line' : 'ri-file-copy-line'}></i>
                  {mylinksCopied ? 'Copié' : 'Copier'}
                </button>
                <a
                  href={myLinksUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 flex items-center justify-center rounded-md bg-background-50 border border-background-200/70 text-foreground-500 hover:text-primary-600 transition-colors flex-shrink-0"
                  title="Ouvrir ma page MyLinks"
                >
                  <i className="ri-external-link-line text-sm"></i>
                </a>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mb-6 md:mb-8">
        <h3 className="text-sm font-semibold text-foreground-700 mb-3">Actions rapides</h3>
        <div className="flex flex-wrap gap-2">
          {([
            { icon: 'ri-sun-line', label: 'Mon activité du jour', link: '/dashboard/activite', color: 'bg-primary-500 text-background-50 hover:bg-primary-600' },
            { icon: 'ri-add-line', label: 'Ajouter un produit', link: '/dashboard/products', color: 'bg-foreground-950 text-background-50 hover:bg-foreground-800' },
            { icon: 'ri-service-line', label: 'Créer un service', link: '/dashboard/services', color: 'bg-accent-500 text-background-50 hover:bg-accent-600' },
            { icon: 'ri-calendar-event-line', label: 'Événements', link: '/dashboard/events', color: 'bg-background-50 border border-background-200/70 text-foreground-700 hover:bg-background-100' },
            { icon: 'ri-graduation-cap-line', label: 'Formations', link: '/dashboard/formation', color: 'bg-background-50 border border-background-200/70 text-foreground-700 hover:bg-background-100' },
            ...(caps.services ? [{ icon: 'ri-briefcase-line', label: 'Portfolio', link: '/dashboard/services/portfolio', color: 'bg-background-50 border border-background-200/70 text-foreground-700 hover:bg-background-100' }] : []),
            { icon: 'ri-robot-2-line', label: 'Recherches IA', link: '/dashboard/socle?tab=notifications', color: 'bg-background-50 border border-background-200/70 text-foreground-700 hover:bg-background-100', badge: stats.aiSearchUnread },
            { icon: 'ri-history-line', label: 'Journal d\'activité', link: '/dashboard/journal', color: 'bg-background-50 border border-background-200/70 text-foreground-700 hover:bg-background-100' },
            { icon: 'ri-global-line', label: 'Mon siteweb', link: '/dashboard/mon-siteweb', color: 'bg-background-50 border border-background-200/70 text-foreground-700 hover:bg-background-100' },
            { icon: 'ri-store-2-line', label: 'Ma boutique', link: '/dashboard/store', color: 'bg-background-50 border border-background-200/70 text-foreground-700 hover:bg-background-100' },
            { icon: 'ri-links-line', label: 'Mon lien MyLinks', link: '/dashboard/store', color: 'bg-accent-100 text-accent-800 hover:bg-accent-200' },
            { icon: 'ri-palette-line', label: 'Statistiques', link: '/dashboard/analytics', color: 'bg-background-50 border border-background-200/70 text-foreground-700 hover:bg-background-100' },
          ] as Array<{ icon: string; label: string; link: string; color: string; badge?: number }>).map((action, i) => (
            <Link
              key={i}
              to={action.link}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${action.color}`}
            >
              <i className={`${action.icon} text-sm`}></i>
              {action.label}
              {typeof action.badge === 'number' && action.badge > 0 && (
                <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-accent-500 text-background-50 text-[10px] font-bold flex items-center justify-center">
                  {action.badge > 99 ? '99+' : action.badge}
                </span>
              )}
            </Link>
          ))}
        </div>
      </div>

      {/* Explorer : raccourcis vers les espaces regroupés du menu */}
      <div className="mb-6 md:mb-8">
        <h3 className="text-sm font-semibold text-foreground-700 mb-3">Explorer mon espace</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
          {([
            { icon: 'ri-store-3-line', label: 'Vendre', desc: 'Produits, commandes, clients', link: '/dashboard/products' },
            { icon: 'ri-wallet-3-line', label: 'Argent', desc: 'Portefeuille & paiements', link: '/dashboard/wallet' },
            { icon: 'ri-global-line', label: 'Mon site', desc: 'Thèmes et pages', link: '/dashboard/mon-siteweb' },
            { icon: 'ri-megaphone-line', label: 'Communication', desc: 'Messages, missions', link: '/dashboard/messages' },
            { icon: 'ri-line-chart-line', label: 'Croissance', desc: 'Statistiques & apps', link: '/dashboard/analytics' },
            { icon: 'ri-settings-3-line', label: 'Réglages', desc: 'Contenus & paramètres', link: '/dashboard/settings' },
          ] as Array<{ icon: string; label: string; desc: string; link: string }>).map((item) => (
            <Link
              key={item.label}
              to={item.link}
              className="group bg-background-50 border border-background-200/70 rounded-lg p-4 hover:border-primary-300/70 hover:bg-primary-50/30 transition-colors cursor-pointer"
            >
              <span className="w-9 h-9 rounded-lg bg-background-100 text-foreground-700 flex items-center justify-center mb-2 group-hover:bg-primary-100 group-hover:text-primary-700 transition-colors">
                <i className={`${item.icon} text-lg`}></i>
              </span>
              <p className="text-sm font-semibold text-foreground-900">{item.label}</p>
              <p className="text-[11px] text-foreground-500 mt-0.5">{item.desc}</p>
            </Link>
          ))}
        </div>
      </div>

    </div>
  );
}